import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import toast from 'react-hot-toast'
import {
  getPublicTournament,
  getPublicTournamentMatches,
  getMyParticipantForTournament,
  submitPublicPrediction,
} from '../services/publicService'
import type { PublicTournament } from '../types/public'
import type { PublicParticipant } from '../types/public'
import { ScoreEnum, PredictionTypeEnum, getValueByPredictionType } from '../types/prediction'

interface MatchItem {
  id: string
  date: string
  time: string
  teamA: string
  teamB: string
  league: string
  score: string | null
  winner: string | null
}

const PublicHub: React.FC = () => {
  const { publicTournamentId } = useParams<{ publicTournamentId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [tournament, setTournament] = useState<PublicTournament | null>(null)
  const [participant, setParticipant] = useState<PublicParticipant | null>(null)
  const [matches, setMatches] = useState<MatchItem[]>([])
  const [loading, setLoading] = useState(true)
  const [predictingMatchId, setPredictingMatchId] = useState<string | null>(null)
  const [winner, setWinner] = useState<string | null>(null)
  const [predictedScore, setPredictedScore] = useState<string>(ScoreEnum.OneZero)

  useEffect(() => {
    if (!publicTournamentId) return
    const load = async () => {
      setLoading(true)
      try {
        const [tData, pData, mData] = await Promise.all([
          getPublicTournament(publicTournamentId),
          getMyParticipantForTournament(publicTournamentId),
          getPublicTournamentMatches(publicTournamentId),
        ])
        setTournament(tData)
        setParticipant(pData)
        setMatches(mData)
      } catch (e) {
        toast.error('Failed to load')
        navigate('/')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [publicTournamentId, navigate])

  const handleSubmitPrediction = async (matchId: string) => {
    if (!publicTournamentId || !participant || !winner) return
    let scoreToSend = predictedScore
    if (winner === 'teamB' && predictedScore.includes('-')) {
      const [a, b] = predictedScore.split('-').map((s) => s.trim())
      scoreToSend = `${b}-${a}`
    }
    const match = matches.find((m) => m.id === matchId)
    const winnerTeam =
      winner === 'teamA' ? (match?.teamA ?? '') : winner === 'teamB' ? (match?.teamB ?? '') : null
    setPredictingMatchId(matchId)
    try {
      await submitPublicPrediction(publicTournamentId, {
        publicParticipantId: participant.id,
        matchId,
        winner: winnerTeam ?? undefined,
        predictedScore: scoreToSend,
      })
      toast.success('Prediction saved')
      setPredictingMatchId(null)
      setWinner(null)
      setPredictedScore(ScoreEnum.OneZero)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save')
    } finally {
      setPredictingMatchId(null)
    }
  }

  const scoreOptions =
    winner === 'draw' || winner === null
      ? getValueByPredictionType(PredictionTypeEnum.Draw)
      : getValueByPredictionType(PredictionTypeEnum.Win)

  if (loading || !tournament) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  if (!participant) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 container mx-auto px-4 pb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
          </Link>
          <div className="glass-card rounded-2xl p-8 text-center max-w-md mx-auto">
            <p className="text-foreground mb-4">You are not enrolled in this public tournament.</p>
            <Link to="/dashboard" className="btn-primary px-4 py-2 rounded-xl">Go to Dashboard</Link>
          </div>
        </main>
      </div>
    )
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const upcomingMatches = matches.filter((m) => {
    const matchDate = new Date(m.date)
    matchDate.setHours(0, 0, 0, 0)
    const isFutureOrToday = matchDate >= today
    const isNotFinished = !m.score || !m.winner
    return isFutureOrToday && isNotFinished
  })

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 container mx-auto px-4 pb-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={18} /> {t('dashboard.backToDashboard')}
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">{tournament.name}</h1>
          <Link
            to={`/public/${publicTournamentId}/leaderboard`}
            className="inline-flex items-center px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium"
          >
            {t('publicHub.leaderboard')}
          </Link>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {tournament.gameType === 'football' ? t('publicHub.upcomingMatchesNext10Days') : t('publicHub.upcomingMatches')}
        </p>

        <div className="space-y-4">
          {upcomingMatches.length === 0 ? (
            <p className="text-muted-foreground">{t('publicHub.noUpcomingMatches')}</p>
          ) : (
            upcomingMatches.map((match) => (
              <div key={match.id} className="glass-card rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="font-medium text-foreground">{match.teamA} vs {match.teamB}</span>
                  <span className="text-sm text-muted-foreground">{match.date} {match.time}</span>
                </div>
                <div className="flex flex-wrap gap-2 items-center mt-2">
                  <select
                    value={winner ?? ''}
                    onChange={(e) => setWinner(e.target.value || null)}
                    className="px-2 py-1 rounded-lg border border-muted bg-background text-foreground text-sm"
                  >
                    <option value="">-- Winner --</option>
                    <option value="teamA">{match.teamA}</option>
                    <option value="teamB">{match.teamB}</option>
                    <option value="draw">Draw</option>
                  </select>
                  <select
                    value={predictedScore}
                    onChange={(e) => setPredictedScore(e.target.value)}
                    className="px-2 py-1 rounded-lg border border-muted bg-background text-foreground text-sm"
                  >
                    {scoreOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleSubmitPrediction(match.id)}
                    disabled={!winner || predictingMatchId === match.id}
                    className="px-3 py-1 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
                  >
                    {predictingMatchId === match.id ? t('common.loading') : t('publicHub.makePrediction')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  )
}

export default PublicHub
